<template>
  <form @submit.prevent="$emit('submit', company)" class="form">
    <div v-if="company.deleted_at != null" class="form-wrap">
      <div class="alert-container alert alert-danger">
        <span>{{ $t('companies.deleted') }}</span>
        <button type="button" class="button button-red--outline button-round button-small" @click="undoRemove">
          {{ $t('companies.deleteUndo') }}
        </button>
      </div>
    </div>
    <div class="form-wrap">
      <label for="name">{{ $t('meta.name') }}</label>
      <input v-model="company.name" class="form-input" type="text" name="name" id="name" />
    </div>

    <div class="form-wrap select-wrap">
      <label for="company_type">{{ $t('companies.companyType') }}</label>
      <select v-model="company.company_type" class="select-wrap">
        <option value="branch">{{ $t('companies.companyTypeBranch') }}</option>
        <option value="industry">{{ $t('companies.companyTypeIndustry') }}</option>
      </select>
    </div>

    <div v-if="company.company_type == 'industry'">
      <div class="form-wrap">
        <label for="order_types">{{ $t('companies.orderTypes') }}</label>
        <div class="form-control-container">
          <FormMultiCheckboxes :items="orderTypeOptions" v-model="company.settings.available_order_types" />
        </div>
      </div>
      <div class="form-wrap">
        <label for="order_types_binity_order_app">{{ $t('companies.orderTypesInBinityOrderApp') }}</label>
        <div class="form-control-container">
          <div class="info-box">
            <span class="material-icons">info</span>
            {{ $t('companies.orderTypesInBinityOrderAppInfoText') }}
          </div>
          <FormMultiCheckboxes
            :items="orderTypeOptionsForBinityOrderApp"
            v-model="company.settings.available_order_types_for_binity_order_app"
          />
        </div>
      </div>
      <div class="form-wrap">
        <p class="label">{{ $t('companies.enableOrderTemplates.headline') }}</p>
        <div class="form-control-container">
          <div class="info-box">
            <span class="material-icons">info</span>
            {{ $t('companies.enableOrderTemplates.description') }}
          </div>
          <SingleCheckbox
            v-model="company.settings.enable_order_templates"
            :label="$t('companies.enableOrderTemplates.label')"
            :name="'enableOrderTemplates'"
          />
        </div>
      </div>
      <div class="form-wrap">
        <p class="label">{{ $t('companies.followUpOrderTypes.headline') }}</p>
        <div class="form-control-container">
          <div class="info-box">
            <span class="material-icons">info</span>
            {{ $t('companies.followUpOrderTypes.description') }}
          </div>
          <FollowUpOrderTypes v-model="company.settings.default_follow_up_order_types" />
        </div>
      </div>
      <div class="form-wrap">
        <p class="label">{{ $t('meta.gatheringOrderWeighAndEmptyActionOptional.headline') }}</p>
        <div class="form-control-container">
          <div class="info-box">
            <span class="material-icons">info</span>
            {{ $t('meta.gatheringOrderWeighAndEmptyActionOptional.description') }}
          </div>
          <SingleCheckbox
            v-model="company.settings.gathering_order_weigh_and_empty_action_optional"
            :label="$t('meta.gatheringOrderWeighAndEmptyActionOptional.label')"
            :name="'gatheringOrderWeighAndEmptyActionOptional'"
          />
        </div>
      </div>
      <div class="form-wrap">
        <p class="label">{{ $t('meta.disableLocationDropdownInOrderApp.headline') }}</p>
        <div class="form-control-container">
          <div class="info-box">
            <span class="material-icons">info</span>
            {{ $t('meta.disableLocationDropdownInOrderApp.description') }}
          </div>
          <SingleCheckbox
            v-model="company.settings.disable_location_dropdown_in_order_app"
            :label="$t('meta.disableLocationDropdownInOrderApp.label')"
            :name="'disableLocationDropdownInOrderApp'"
          />
        </div>
      </div>
      <div class="form-wrap">
        <p class="label">{{ $t('companies.enableFractionDisposalSelection.headline') }}</p>
        <div class="form-control-container">
          <div class="info-box">
            <span class="material-icons">info</span>
            {{ $t('companies.enableFractionDisposalSelection.description') }}
          </div>
          <SingleCheckbox
            v-model="company.settings.enable_fraction_disposal_selection"
            :label="$t('companies.enableFractionDisposalSelection.label')"
            :name="'enableFractionDisposalSelection'"
          />
        </div>
      </div>
      <div class="form-wrap">
        <p class="label">{{ $t('companies.enableGatheringOrderCheckAction.headline') }}</p>
        <div class="form-control-container">
          <div class="info-box">
            <span class="material-icons">info</span>
            {{ $t('companies.enableGatheringOrderCheckAction.description') }}
          </div>
          <SingleCheckbox
            v-model="company.settings.enable_gathering_order_check_action"
            :label="$t('companies.enableGatheringOrderCheckAction.label')"
            :name="'enableGatheringOrderCheckAction'"
          />
        </div>
      </div>
      <div class="form-wrap">
        <p class="label">{{ $t('companies.enableGatheringOrderCountPage.headline') }}</p>
        <div class="form-control-container">
          <div class="info-box">
            <span class="material-icons">info</span>
            {{ $t('companies.enableGatheringOrderCountPage.description') }}
          </div>
          <SingleCheckbox
            v-model="company.settings.enable_gathering_order_count_page"
            :label="$t('companies.enableGatheringOrderCountPage.label')"
            :name="'enableGatheringOrderCountPage'"
          />
        </div>
      </div>
      <div class="form-wrap">
        <p class="label">{{ $t('companies.reportingType.headline') }}</p>
        <div class="form-control-container">
          <div class="info-box">
            <span class="material-icons">info</span>
            {{ $t('companies.reportingType.infoText') }}
          </div>
          <div class="select-wrap">
            <label for="reporting-type-select">{{ $t('companies.reportingType.selectLabel') }}</label>
            <select id="reporting-type-select" v-model="company.settings.reporting_type">
              <option value="" disabled>{{ $t('meta.pleaseSelect') }}</option>
              <option value="actions">{{ $t('companies.reportingType.actions') }}</option>
              <option value="rums">{{ $t('companies.reportingType.rums') }}</option>
            </select>
          </div>
        </div>
      </div>
      <div class="form-wrap">
        <p class="label">{{ $t('companies.allowSamePickupOrderZones.headline') }}</p>
        <div class="form-control-container">
          <div class="info-box">
            <span class="material-icons">info</span>
            {{ $t('companies.allowSamePickupOrderZones.description') }}
          </div>
          <SingleCheckbox
            v-model="company.settings.allow_same_pickup_order_zones"
            :label="$t('companies.allowSamePickupOrderZones.label')"
            :name="'allowSamePickupOrderZones'"
          />

          <div class="info-box" style="margin-top: 2.5em">
            <span class="material-icons">info</span>
            {{ $t('companies.allowPickupOrderTargetZoneOverride.description') }}
          </div>
          <SingleCheckbox
            v-model="company.settings.allow_pickup_order_target_zone_override"
            :label="$t('companies.allowPickupOrderTargetZoneOverride.label')"
            :name="'allowPickupOrderTargetZoneOverride'"
          />
        </div>
      </div>
    </div>

    <input
      type="submit"
      :value="$t('companies.saveCompany')"
      :disabled="company.deleted_at != null"
      class="button button-green button-round"
    />
  </form>
</template>

<script>
import FollowUpOrderTypes from '@/views/admin/companies/FollowUpOrderTypes'

import FormMultiCheckboxes from '@/components/Form/Checkbox/MultiCheckboxes'
import SingleCheckbox from '@/components/Form/Checkbox/SingleCheckbox'

import { ORDER_TYPE_OPTIONS } from '@/api/orders'

export default {
  components: {
    FormMultiCheckboxes,
    FollowUpOrderTypes,
    SingleCheckbox,
  },
  data: () => ({
    company: null,
  }),
  props: {
    initialCompany: {
      type: Object,
      default: () => ({ settings: { reporting_type: 'actions' } }),
    },
  },
  computed: {
    orderTypeOptions() {
      return ORDER_TYPE_OPTIONS.map(o => ({ label: this.$t(o.label_i18n), value: o.value }))
    },
    orderTypeOptionsForBinityOrderApp() {
      return ORDER_TYPE_OPTIONS.map(option => {
        return {
          label: this.$t(option.label_i18n),
          value: option.value,
          disabled: !this.company?.settings?.available_order_types?.includes(option.value),
        }
      })
    },
  },
  watch: {
    initialCompany: {
      handler() {
        this.company = { ...this.initialCompany }
      },
      immediate: true,
    },
    'company.settings.available_order_types': {
      handler() {
        if (!this.company?.settings?.available_order_types_for_binity_order_app) return
        this.company.settings.available_order_types_for_binity_order_app =
          this.company.settings.available_order_types_for_binity_order_app.filter(type =>
            this.company.settings.available_order_types.includes(type),
          )
      },
      deep: true,
    },
  },
}
</script>
